<script setup>
import { ref, watch } from "vue";
import axios from "axios";
import ViewItemSectionTitle from "@/components/utils/ViewItemSectionTitle.vue";

const props = defineProps({
  property: {
    type: Object,
    required: true,
  },
  showGoBack: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["goBackClicked"]);

// Document viewer
const docViewerDialog = ref(false);
const docLoading = ref(false);
const viewerKey = ref(0);
const viewingDoc = ref({
  pn: "",
  url: "",
  downloadUrl: "",
  filename: "",
  isPdf: false,
});

let _currentBlobUrl = null;

function _revokeBlobUrl() {
  if (_currentBlobUrl) {
    URL.revokeObjectURL(_currentBlobUrl);
    _currentBlobUrl = null;
  }
}

// Clean up blob URL when dialog is closed
watch(docViewerDialog, (isOpen) => {
  if (!isOpen) {
    _revokeBlobUrl();
    viewingDoc.value = { pn: "", url: "", downloadUrl: "", filename: "", isPdf: false };
  }
});

async function viewDocument(item) {
  if (!item.title_deed_scan_url) return;

  const apiUrl = `${process.env.VUE_APP_API_URL}/api/client/scheme-plan-property/${item.id}/title-deed`;
  const downloadUrl = `${apiUrl}?download=1`;
  const ext = item.title_deed_scan_url.split('?')[0].split('.').pop().toLowerCase();
  const isPdf = ext === 'pdf';
  const filename = item.title_deed_scan_url.split('/').pop().split('?')[0] || `title-deed-${item.pn}`;

  _revokeBlobUrl();
  viewerKey.value++;
  viewingDoc.value = { pn: item.pn, url: "", downloadUrl, filename, isPdf };
  docLoading.value = true;
  docViewerDialog.value = true;

  try {
    const response = await axios.get(apiUrl, {
      responseType: "blob",
      headers: { Authorization: localStorage.getItem("user-token") },
    });
    const blobUrl = URL.createObjectURL(response.data);
    _currentBlobUrl = blobUrl;
    viewingDoc.value = { ...viewingDoc.value, url: blobUrl };
  } catch (e) {
    console.error("Failed to load title deed document", e);
  } finally {
    docLoading.value = false;
  }
}

async function downloadDocument() {
  const { downloadUrl, filename } = viewingDoc.value;
  if (!downloadUrl) return;
  try {
    const response = await axios.get(downloadUrl, {
      responseType: "blob",
      headers: { Authorization: localStorage.getItem("user-token") },
    });
    const url = URL.createObjectURL(response.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error("Failed to download title deed document", e);
  }
}

function formatDate(date) {
  if (!date) return "—";
  const d = new Date(date);
  return `${String(d.getDate()).padStart(2, "0")}/${String(
    d.getMonth() + 1
  ).padStart(2, "0")}/${d.getFullYear()}`;
}

function formatDateTime(date) {
  if (!date) return "—";
  const d = new Date(date);
  return `${String(d.getDate()).padStart(2, "0")}/${String(
    d.getMonth() + 1
  ).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(
    2,
    "0"
  )}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function formatNumber(value) {
  if (value == null) return "—";
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
</script>

<template>
  <v-container fluid class="pa-0">
    <!-- Go Back & Title -->
    <v-row v-if="showGoBack">
      <v-col cols="1">
        <v-btn plain @click="$emit('goBackClicked')">
          <v-icon left>mdi-arrow-left</v-icon>
          <span class="text-caption text-none">Back to list</span>
        </v-btn>
      </v-col>
      <v-col cols="10" class="text-h6 primary--text">
        <div class="text-center">View Property</div>
      </v-col>
      <v-spacer />
    </v-row>

    <v-row dense>
      <v-col cols="auto">
        <ViewItemSectionTitle>Property Details</ViewItemSectionTitle>
      </v-col>
    </v-row>

    <v-row>
      <!-- Left Column -->
      <v-col cols="12" md="6">
        <v-simple-table dense>
          <template #default>
            <tbody>
              <tr>
                <td class="font-weight-bold">PN</td>
                <td>{{ property.pn }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">LR Number</td>
                <td>{{ property.lr_number || "N/A" }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Title Deed PN</td>
                <td>{{ property.title_deed_pn || "N/A" }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Owner</td>
                <td>{{ property.client ? property.client.customer_name : "N/A" }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Size (m²)</td>
                <td>{{ formatNumber(property.size_sqm) }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Source</td>
                <td>{{ property.source || "N/A" }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Title Deed</td>
                <td>
                  <v-icon
                    small
                    :color="property.title_deed_scan_url ? 'success' : 'grey lighten-1'"
                  >
                    {{ property.title_deed_scan_url ? "mdi-file-check" : "mdi-file-remove-outline" }}
                  </v-icon>
                  <v-btn
                    v-if="property.title_deed_scan_url"
                    text
                    class="primary--text text-none ml-1"
                    @click="viewDocument(property)"
                  >
                    View Document
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>

      <!-- Right Column -->
      <v-col cols="12" md="6">
        <v-simple-table dense>
          <template #default>
            <tbody>
              <tr>
                <td class="font-weight-bold">Sub County</td>
                <td>{{ property.sub_county ? property.sub_county.sub_county_name : "N/A" }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Ward</td>
                <td>{{ property.ward ? property.ward.ward_name : "N/A" }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Town</td>
                <td>{{ property.town ? property.town.town_name : "N/A" }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Physical Location</td>
                <td>{{ property.physical_location || "N/A" }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Latitude</td>
                <td>{{ property.latitude || "N/A" }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Longitude</td>
                <td>{{ property.longitude || "N/A" }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Created</td>
                <td>{{ formatDateTime(property.created_at) }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Last Updated</td>
                <td>{{ formatDateTime(property.updated_at) }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>
    </v-row>

    <!-- Document Viewer Dialog -->
    <v-dialog
      v-model="docViewerDialog"
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-toolbar dense color="primary" dark>
          <v-toolbar-title>
            Title Deed — {{ viewingDoc.pn }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            :disabled="docLoading"
            title="Download"
            @click="downloadDocument"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
          <v-btn icon @click="docViewerDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-2">
          <div v-if="docLoading" class="d-flex justify-center align-center" style="height: 80vh">
            <v-progress-circular indeterminate color="primary" size="64" />
          </div>
          <div v-else align="center">
            <!-- PDF viewer -->
            <iframe
              v-if="viewingDoc.isPdf"
              :key="viewerKey"
              :src="viewingDoc.url"
              style="width: 100%; height: 85vh; border: none"
            ></iframe>
            <!-- Image viewer -->
            <v-img
              v-else
              :key="viewerKey"
              :src="viewingDoc.url"
              contain
              max-height="90vh"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>
